import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { Download, FileBarChart, Printer } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { APP_SHORT_NAME } from "@/config/constants";
import type { LocacaoDto, MaquinarioDto, PeriodRange } from "@/services/locacoes";
import {
  CONTRATO_STATUS_LABELS,
  fetchRelatorioAbastecimentos,
  fetchRelatorioConsumo,
  fetchRelatorioLocacoes,
  fetchRelatorioUtilizacao,
} from "@/services/locacoes";
import { formatDate, formatDateTime } from "@/utils/format";
import { downloadCsv, formatHoras } from "./locacoesUtils";

type ReportKind = "utilizacao" | "abastecimentos" | "consumo" | "contratos" | "completo";

interface ReportSection {
  title: string;
  headers: string[];
  rows: string[][];
}

interface ReportPreview {
  title: string;
  sections: ReportSection[];
}

interface Props {
  reportPeriod: PeriodRange;
  maquinarios: MaquinarioDto[];
  contratoId?: string;
  /** Incrementa após mutações no snapshot — invalida preview desatualizado. */
  dataVersion: number;
}

export function TabRelatorios({ reportPeriod, maquinarios, contratoId, dataVersion }: Props) {
  const [kind, setKind] = useState<ReportKind>("utilizacao");
  const [maquinaId, setMaquinaId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ReportPreview | null>(null);
  const [printGeneratedAt, setPrintGeneratedAt] = useState<string | null>(null);

  const scopedMaquinaIds = maquinarios.map((m) => m.id);

  useEffect(() => {
    setPreview(null);
    setPrintGeneratedAt(null);
  }, [dataVersion, reportPeriod.startDate, reportPeriod.endDate, contratoId]);

  const reportScopeOpts = () => {
    const base = contratoId ? { contratoId } : {};
    if (maquinaId) return { ...base, maquinaId };
    if (scopedMaquinaIds.length > 0 && scopedMaquinaIds.length < 999) {
      return { ...base, maquinaIds: scopedMaquinaIds };
    }
    return base;
  };

  const runReport = async () => {
    setLoading(true);
    setError(null);
    try {
      if (kind === "utilizacao") {
        const data = await fetchRelatorioUtilizacao(reportPeriod, reportScopeOpts());
        setPreview({
          title: "Relatório de Utilização",
          sections: [
            {
              title: "Utilização (horas)",
              headers: ["Máquina", "Horas trabalhadas"],
              rows: data.rows.map((r) => [r.maquinaNome, formatHoras(r.horasTrabalhadas)]),
            },
          ],
        });
      } else if (kind === "abastecimentos") {
        const data = await fetchRelatorioAbastecimentos(reportPeriod, reportScopeOpts());
        setPreview({
          title: "Relatório de Abastecimentos",
          sections: [
            {
              title: "Abastecimentos",
              headers: ["Máquina", "Litros", "Valor", "Data", "Responsável"],
              rows: data.rows.map((r) => [
                r.maquinaNome,
                String(r.litros),
                String(r.valorTotal),
                r.data,
                r.usuarioNome,
              ]),
            },
          ],
        });
      } else if (kind === "consumo") {
        const data = await fetchRelatorioConsumo(
          reportPeriod,
          maquinaId ? [maquinaId] : scopedMaquinaIds.length ? scopedMaquinaIds : undefined,
          contratoId,
        );
        const sections: ReportSection[] = [
          {
            title: "Consumo por máquina (L/h)",
            headers: ["Máquina", "Litros", "Horas", "L/h", "Limite L/h"],
            rows: (data.rankingMaquinas ?? []).map((r) => [
              r.maquinaNome,
              String(r.litros),
              String(r.horas),
              String(r.litrosPorHora),
              String(r.consumoLimiteLph),
            ]),
          },
        ];
        if ((data.ranking ?? []).length > 0) {
          sections.push({
            title: "Consumo por contrato (L/h)",
            headers: ["Contrato", "Cliente", "Litros/hora"],
            rows: data.ranking.map((r) => [
              r.codigoContrato,
              r.clienteSiengeNome,
              String(r.litrosPorHora),
            ]),
          });
        }
        setPreview({
          title: "Relatório de Consumo (L/h)",
          sections,
        });
      } else if (kind === "contratos") {
        const data = await fetchRelatorioLocacoes(reportPeriod, { contratoId });
        setPreview({
          title: "Relatório de Contratos / Locações",
          sections: [
            {
              title: "Contratos / locações",
              headers: ["Máquina", "Cliente", "Modelo", "Início", "Fim prev.", "Status"],
              rows: data.rows.map((r) => {
                const row = r as LocacaoDto & {
                  clienteSiengeNome?: string;
                  modeloContrato?: string;
                };
                return [
                  row.maquinaNome ?? "—",
                  row.clienteSiengeNome ?? row.costCenterLabel ?? "—",
                  row.modeloContrato ?? "—",
                  row.dataInicio ? formatDate(row.dataInicio) : "—",
                  row.dataFimPrevista ? formatDate(row.dataFimPrevista) : "—",
                  CONTRATO_STATUS_LABELS[row.status as keyof typeof CONTRATO_STATUS_LABELS] ?? row.status,
                ];
              }),
            },
          ],
        });
      } else {
        const scope = reportScopeOpts();
        const maqIds = maquinaId ? [maquinaId] : scopedMaquinaIds.length ? scopedMaquinaIds : undefined;
        const [util, abast, consumo, contratosData] = await Promise.all([
          fetchRelatorioUtilizacao(reportPeriod, scope),
          fetchRelatorioAbastecimentos(reportPeriod, scope),
          fetchRelatorioConsumo(reportPeriod, maqIds, contratoId),
          fetchRelatorioLocacoes(reportPeriod, { contratoId }),
        ]);
        setPreview({
          title: "Relatório completo — Semog Locações",
          sections: [
            {
              title: "Utilização (horas)",
              headers: ["Máquina", "Horas trabalhadas"],
              rows: util.rows.map((r) => [r.maquinaNome, formatHoras(r.horasTrabalhadas)]),
            },
            {
              title: "Abastecimentos",
              headers: ["Máquina", "Litros", "Valor", "Data", "Responsável"],
              rows: abast.rows.map((r) => [
                r.maquinaNome,
                String(r.litros),
                String(r.valorTotal),
                r.data,
                r.usuarioNome,
              ]),
            },
            {
              title: "Consumo por máquina (L/h)",
              headers: ["Máquina", "Litros", "Horas", "L/h", "Limite L/h"],
              rows: (consumo.rankingMaquinas ?? []).map((r) => [
                r.maquinaNome,
                String(r.litros),
                String(r.horas),
                String(r.litrosPorHora),
                String(r.consumoLimiteLph),
              ]),
            },
            {
              title: "Consumo por contrato (L/h)",
              headers: ["Contrato", "Cliente", "Litros/hora"],
              rows: consumo.ranking.map((r) => [
                r.codigoContrato,
                r.clienteSiengeNome,
                String(r.litrosPorHora),
              ]),
            },
            {
              title: "Contratos / locações",
              headers: ["Máquina", "Cliente", "Modelo", "Início", "Fim prev.", "Status"],
              rows: contratosData.rows.map((r) => {
                const row = r as LocacaoDto & {
                  clienteSiengeNome?: string;
                  modeloContrato?: string;
                };
                return [
                  row.maquinaNome ?? "—",
                  row.clienteSiengeNome ?? row.costCenterLabel ?? "—",
                  row.modeloContrato ?? "—",
                  row.dataInicio ? formatDate(row.dataInicio) : "—",
                  row.dataFimPrevista ? formatDate(row.dataFimPrevista) : "—",
                  CONTRATO_STATUS_LABELS[row.status as keyof typeof CONTRATO_STATUS_LABELS] ?? row.status,
                ];
              }),
            },
          ],
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao gerar relatório.");
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const totalRows = preview?.sections.reduce((n, s) => n + s.rows.length, 0) ?? 0;

  const exportExcel = () => {
    if (!preview) return;
    const headers = preview.sections[0]?.headers ?? [];
    const rows: string[][] = [];
    for (const section of preview.sections) {
      if (preview.sections.length > 1) {
        rows.push([`--- ${section.title} ---`, ...Array(Math.max(0, headers.length - 1)).fill("")]);
      }
      rows.push(...section.rows);
    }
    downloadCsv(
      `${preview.title.replace(/\s+/g, "_")}_${reportPeriod.startDate}_${reportPeriod.endDate}.csv`,
      headers,
      rows,
    );
  };

  const exportPdf = () => {
    if (!preview) return;
    const generatedAt = new Date().toISOString();
    flushSync(() => setPrintGeneratedAt(generatedAt));
    window.print();
  };

  return (
    <div className="space-y-4">
      <div className="no-print rounded-xl border border-slate-200 bg-white p-5 shadow-card">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-bold text-slate-800">
          <FileBarChart className="h-4 w-4 text-brand-600" />
          Gerar relatório
          {contratoId && (
            <span className="text-xs font-normal text-slate-500">
              (período selecionado — {formatDate(reportPeriod.startDate)} a{" "}
              {formatDate(reportPeriod.endDate)})
            </span>
          )}
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Select
            label="Tipo de relatório"
            value={kind}
            onChange={(e) => setKind(e.target.value as ReportKind)}
            options={[
              { value: "utilizacao", label: "Utilização (horas)" },
              { value: "abastecimentos", label: "Abastecimentos" },
              { value: "consumo", label: "Consumo (L/h)" },
              { value: "contratos", label: "Contratos / locações" },
              { value: "completo", label: "Completo (todos os dados)" },
            ]}
          />
          {(kind === "utilizacao" || kind === "abastecimentos" || kind === "consumo" || kind === "completo") && (
            <Select
              label="Máquina (opcional)"
              value={maquinaId}
              onChange={(e) => setMaquinaId(e.target.value)}
              options={[
                { value: "", label: maquinarios.length ? `Todas (${maquinarios.length})` : "Todas" },
                ...maquinarios.map((m) => ({ value: m.id, label: m.nome })),
              ]}
            />
          )}
          <div className="flex items-end">
            <Button onClick={() => void runReport()} loading={loading} className="w-full md:w-auto">
              Gerar preview
            </Button>
          </div>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </div>

      {preview && (
        <div className="locacoes-report-print rounded-xl border border-slate-200 bg-white shadow-card">
          <header className="print-only locacoes-report-print-header">
            <div className="locacoes-report-print-brand">
              <div className="locacoes-report-print-logo" aria-hidden="true">
                S
              </div>
              <div>
                <p className="locacoes-report-print-company">{APP_SHORT_NAME} Engenharia</p>
                <p className="locacoes-report-print-module">Semog Locações — Relatório operacional</p>
              </div>
            </div>
            <div className="locacoes-report-print-meta">
              <p>
                <strong>Gerado em:</strong>{" "}
                {formatDateTime(printGeneratedAt ?? new Date().toISOString())}
              </p>
              <p>
                <strong>Período:</strong> {formatDate(reportPeriod.startDate)} – {formatDate(reportPeriod.endDate)}
              </p>
            </div>
          </header>

          <div className="no-print flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
            <h4 className="text-sm font-bold text-slate-800">{preview.title}</h4>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" leftIcon={<Download className="h-3.5 w-3.5" />} onClick={exportExcel}>
                Excel (CSV)
              </Button>
              <Button size="sm" variant="ghost" leftIcon={<Printer className="h-3.5 w-3.5" />} onClick={exportPdf}>
                PDF (imprimir)
              </Button>
            </div>
          </div>

          <div className="locacoes-report-print-body px-4 py-3">
            <h1 className="print-only locacoes-report-print-title">{preview.title}</h1>
            <p className="print-only locacoes-report-print-period">
              Período analisado: {formatDate(reportPeriod.startDate)} a {formatDate(reportPeriod.endDate)}
              {contratoId ? " • Histórico completo do contrato" : ""}
            </p>

            {preview.sections.map((section) => (
              <section key={section.title} className="border-b border-slate-100 last:border-b-0">
                {preview.sections.length > 1 && (
                  <>
                    <h5 className="no-print border-b border-slate-50 bg-slate-50/80 px-4 py-2 text-xs font-bold uppercase tracking-wide text-slate-600">
                      {section.title}
                    </h5>
                    <h2 className="print-only locacoes-report-section-title">{section.title}</h2>
                  </>
                )}
                <div className="locacoes-report-table-wrap overflow-x-auto">
                  <table className="locacoes-report-table min-w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        {section.headers.map((h) => (
                          <th key={h} className="px-4 py-3">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {section.rows.length === 0 ? (
                        <tr>
                          <td colSpan={section.headers.length} className="px-4 py-6 text-center text-slate-500">
                            Sem dados no período.
                          </td>
                        </tr>
                      ) : (
                        section.rows.map((row, i) => (
                          <tr key={i} className="hover:bg-slate-50/80">
                            {row.map((cell, j) => (
                              <td key={j} className="px-4 py-3">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>

          <p className="no-print border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
            {totalRows} registro(s) • Período {formatDate(reportPeriod.startDate)} –{" "}
            {formatDate(reportPeriod.endDate)}
          </p>

          <footer className="print-only locacoes-report-print-footer">
            {totalRows} registro(s) • {APP_SHORT_NAME} Locações • Impresso em{" "}
            {formatDateTime(printGeneratedAt ?? new Date().toISOString())}
          </footer>
        </div>
      )}
    </div>
  );
}
